/**
 * API service for the Settings page
 */
import { apiRequest } from "../../services/apiMiddleware";
import { API_GATEWAY_PROCESS_NAME } from "../../utils/constants";
import { getUserName } from "../../services/boson/bosonClient";
import {
  GetIndirectStaffingConfigResponse,
  UpdateIndirectStaffingConfigRequest,
  UpdateIndirectStaffingConfigResponse,
  PromoteConfigRequest,
  PromoteConfigResponse,
  ListConfigDraftsResponse,
  ConfigType,
  GetFlowPathPercentageConfigResponse,
  UpdateWarehouseOperationalConfigRequest,
  UpdateWarehouseOperationalConfigResponse,
} from "./types";

/**
 * Fetch indirect staffing configuration for a warehouse
 * @param warehouseId - The warehouse ID to fetch config for
 * @returns Promise with the indirect staffing config response
 */
export const getIndirectStaffingConfig = async (
  warehouseId: string,
  stageVersionName?: string
): Promise<GetIndirectStaffingConfigResponse> => {
  const requestBody: { warehouseId: string; stageVersionName?: string } = { warehouseId };
  if (stageVersionName) {
    requestBody.stageVersionName = stageVersionName;
  }

  const response = await apiRequest({
    resourcePath: "/getIndirectStaffingConfig",
    httpMethod: "post",
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody,
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || "Failed to fetch indirect staffing config");
  }

  return response.data;
};

/**
 * Update indirect staffing configuration (create, update, or delete rules)
 * @param request - The update request containing warehouseId, changes, etc.
 * @returns Promise with the update response
 */
export const updateIndirectStaffingConfig = async (
  request: UpdateIndirectStaffingConfigRequest
): Promise<UpdateIndirectStaffingConfigResponse> => {
  const userId = await getUserName();
  if (!userId) {
    throw new Error("Unable to determine current user");
  }

  const response = await apiRequest({
    resourcePath: "/updateIndirectStaffingConfig",
    httpMethod: "post",
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: {
      warehouseId: request.warehouseId,
      updatedBy: userId,
      changes: request.changes,
    },
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || "Failed to update indirect staffing config");
  }

  return response.data;
};

/**
 * Promote a draft to live config (generic). Only authorized users can call this.
 */
export const promoteConfig = async (
  request: PromoteConfigRequest
): Promise<PromoteConfigResponse> => {
  const userId = await getUserName();
  if (!userId) {
    throw new Error("Unable to determine current user");
  }

  const response = await apiRequest({
    resourcePath: "/promoteConfig",
    httpMethod: "post",
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: {
      warehouseId: request.warehouseId,
      configType: request.configType,
      stageVersionName: request.stageVersionName,
      promotedBy: userId,
    },
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || "Failed to promote config");
  }

  return response.data;
};

/**
 * List all pending (unpromoted) drafts for a warehouse and config type (generic).
 */
export const listConfigDrafts = async (
  warehouseId: string,
  configType: ConfigType
): Promise<ListConfigDraftsResponse> => {
  const response = await apiRequest({
    resourcePath: "/listConfigDrafts",
    httpMethod: "post",
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: { warehouseId, configType },
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || "Failed to list drafts");
  }

  return response.data;
};

/**
 * Fetch flow path percentage configuration for a warehouse
 * @param warehouseId - The warehouse ID to fetch config for
 * @param stageVersionName - Optional stage version name for draft preview
 * @returns Promise with the flow path percentage config response
 */
export const getFlowPathPercentageConfig = async (
  warehouseId: string,
  stageVersionName?: string
): Promise<GetFlowPathPercentageConfigResponse> => {
  const requestBody: { warehouseId: string; stageVersionName?: string } = { warehouseId };
  if (stageVersionName) {
    requestBody.stageVersionName = stageVersionName;
  }

  const response = await apiRequest({
    resourcePath: "/getFlowPathPercentageConfig",
    httpMethod: "post",
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody,
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || "Failed to fetch flow path percentage config");
  }

  return response.data;
};

/**
 * Update warehouse operational configuration (create, update, or delete config entries)
 * @param request - The update request containing warehouseId, changes, etc.
 * @returns Promise with the update response
 */
export const updateWarehouseOperationalConfig = async (
  request: UpdateWarehouseOperationalConfigRequest
): Promise<UpdateWarehouseOperationalConfigResponse> => {
  const userId = await getUserName();
  if (!userId) {
    throw new Error("Unable to determine current user");
  }

  const response = await apiRequest({
    resourcePath: "/updateWarehouseOperationalConfig",
    httpMethod: "post",
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: {
      warehouseId: request.warehouseId,
      updatedBy: userId,
      changes: request.changes,
    },
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || "Failed to update warehouse operational config");
  }

  return response.data;
};
